import comm from "csi/comm";
import PostRequest from "csi/comm/PostRequest";
import { TYPE_IMAGE } from "csi/model/image";
import sessionConstants from "csi/sessionConstants";
import { FileUploadInfo } from "csi/state/browser";
import { C8AttributeId, C8Node, cnl, IFileData } from "csi/typings";
import _ from "lodash";
import JSZip from "jszip";
import { getFileNameExtension } from "csi/utility/fileUtility";
import { ILocaleProvider } from "designSystem/locale";

const RE_IMAGE = /\.(bmp|gif|jpg|jpeg|png)$/i;
const RE_ZIP = /\.zip$/i;

// The ^(?!.*lo\/) portion is to exclude all files found in the lo folder which contains the low resolution version of the same turntable
const RE_TURNTABLE = /^(?!.*lo\/)(^|.*\/)([1-9][0-9]*)\.(bmp|gif|jpg|jpeg|png)$/i;

/**
 * Check if files with image extensions are really images or not
 * Does not perform any check if extension is not png, gif, jpeg, jpg
 */
export function checkImageSignature(mimeValue: string, fileExtension: string): boolean {
	switch (fileExtension) {
		case "png":
			if (mimeValue !== "89504e47") {
				return false;
			}
			break;
		case "gif":
			if (mimeValue !== "47494638") {
				return false;
			}
			break;
		case "jpeg":
		case "jpg":
			//For jpeg/jpg, FFD8 is the SOI (Start Of Image) marker. It is immediately followed by an EOI (end of image) marker that starts with FF.
			if (mimeValue.length < 8 || mimeValue.substring(0, 6) != "ffd8ff") {
				return false;
			}

			break;
	}

	return true;
}

/** Remove file status attributes from document revisions that haven't been cleaned up yet */
export async function deleteFileStatusAttributes(fileUploadMap: Map<cnl, FileUploadInfo>): Promise<void> {
	if (!fileUploadMap.size) {
		return null;
	}

	const attributeNames = [];
	const nodeCNLs = [];

	for (const [key, info] of fileUploadMap) {
		nodeCNLs.push(info.nodeCNL);
		attributeNames.push(getFileStatusAttributeId(info.attribute));
	}

	const req = new PostRequest("SiteAdmin", "DeleteStatusAttributes");
	req.addParameter("AttributeNames", attributeNames);
	req.addParameter("NodeCNLs", nodeCNLs);
	req.post();
}

/**
 * Delete url created from file object
 */
export function deleteFileData(data: IFileData): void {
	const { file, url } = data;

	if (file && url) {
		URL.revokeObjectURL(url);
	}
}

/**
 * Return file data for given file object; use deleteFileData when finished
 */
export function getFileData(file: File): IFileData {
	const name = getFileName(file);
	const ext = getFileNameExtension(name);
	const url = URL.createObjectURL(file);
	const thumbnail = isImageFileName(name) ? url : null;

	return {
		ext,
		file,
		id: name,
		name,
		thumbnail,
		url,
	};
}

/**
 * Return file data for given file name for Aspera file transfer
 */
export function getAsperaFileData(fileName: string): IFileData {
	const name = fileName;
	const ext = getFileNameExtension(fileName);
	const hstBlob = new Blob([fileName], { type: "text/html" });
	const url = URL.createObjectURL(hstBlob);

	return {
		ext,
		file: null,
		id: name,
		name,
		thumbnail: null,
		url,
	};
}

/**
 * Gets the status attribute that shows the progress bar
 */
export function getFileStatusAttributeId(id: C8AttributeId): C8AttributeId {
	return `${id}.Status`;
}

/**
 * Return file data for given image node
 */
export function getImageFileData(image: C8Node): IFileData {
	const { $Name, $URL, Thumbnail, Viewable } = image;
	return {
		...getPublishedFileData($Name, Viewable),
		imageUrl: $URL,
		thumbnail: comm.getFileHref(Thumbnail),
	};
}

/**
 * Return file data for given file object
 */
export function getPublishedFileData(fileName: string, fileUrl: string): IFileData {
	const ext = getFileNameExtension(fileName);
	const url = comm.getFileHref(fileUrl, false, fileName);

	return {
		ext,
		id: fileUrl,
		name: fileName,
		url,
	};
}

/**
 * Extract file extension from file object
 */
export function getFileExtension(file: File): string {
	return getFileNameExtension(getFileName(file));
}

/**
 * Extract filename from file object
 */
export function getFileName(file: File): string {
	return file.name || getFileNameFromMimeType(file.type) || "";
}

/**
 * Extract filename from MIME type. The MIME type of image has form of “Image/png” or “Image/gif” etc. (“Image/{type}”).
 * This functon converts “image/{type}” to “image.{type}” that can be used as file name. This is used by drag drop functions
 * in the situation when file name is not available and we have to generate new file name based on the image type.
 * IE also adds charset information, separated from image/type with “;”, so this function removes it before returning file name.
 */
export function getFileNameFromMimeType(type: string): string {
	let fileName = type.replace("/", ".");

	// IE adds charset to the file type, have to remove it
	const n = fileName.indexOf(";");
	if (n >= 0) {
		fileName = fileName.substring(0, n);
	}

	return fileName;
}

/**
 * Returns first few characters from file content which contain the mime type information
 */
export async function getFileSignature(file: File): Promise<string> {
	return new Promise<string>((resolve, reject) => {
		const fileReader = new FileReader();
		let header = "";

		fileReader.onerror = (): void => {
			fileReader.abort();
			reject(new DOMException("Problem parsing input file."));
		};

		fileReader.onloadend = (e: ProgressEvent<FileReader>): void => {
			const buffer = e.target.result as ArrayBufferLike;
			const arr = (new Uint8Array(buffer)).subarray(0, 4);

			for (var i = 0; i < arr.length; i++) {
				header += arr[i].toString(16);
			}

			resolve(header);
		};

		fileReader.readAsArrayBuffer(file);
	});
}

/**
 * Extract image number from file name
 */
export function getFileNameNumber(fileName: string, index: number): number {
	return +getTurntablePathGroup(fileName, index);
}

/**
 * Returns a string matching a block of the regex based on the given index
 */
export function getTurntablePathGroup(fileName: string, index: number): string {
	return fileName.match(/(^|.*\/)([1-9][0-9]*)/)[index];
}

/**
 * Return request to publish the files
 */
export function getFileUploadRequest(
	files: File | File[],
	nodeType?: string,
	url?: string,
	nodeAttrId?: string,
	fileAttrId?: string,
	reviseFirst = false,
	prepend = false,
	detectImages = false,
	module?: string,
	operation?: string,
): PostRequest {
	let nodeTypes: string[];

	if (Array.isArray(files)) {
		if (nodeType) {
			nodeTypes.fill(nodeType, 0, files.length);
		} else if (detectImages) {
			nodeTypes = files.map((file) => isImageFile(file) ? TYPE_IMAGE : "");
		}
	}

	return new PostRequest(module || "SiteAdmin", operation || "MultiFilePublish")
		.addParameter("ReviseFirst", reviseFirst)
		.addParameter("Prepend", prepend)
		.addParameter("SessionURL", sessionConstants.sessionUrl)
		.addParameter("NewNodeTypes", nodeTypes?.join(";"))
		.addParameter("NewNodeAttrId", nodeAttrId)
		.addParameter("NewFileAttrId", fileAttrId)
		.addParameter("URL", url)
		.addFiles(files, null, "FormFileFieldName");
}

/**
 * Determine if the given filename is that of an image
 */
export function isImageFile(file: File): boolean {
	return isImageFileName(getFileName(file));
}

/**
 * Determine if the given filename is that of an image
 */
export function isImageFileName(fileName: string): boolean {
	return fileName ? RE_IMAGE.test(fileName) : false;
}

/**
 * Determine if the given file is that of a zip
 */
export function isZipFile(file: File): boolean {
	return isZipFileName(getFileName(file));
}

/**
 * Determine if the given filename is that of an zip
 */
export function isZipFileName(fileName: string): boolean {
	return fileName ? RE_ZIP.test(fileName) : false;
}

/** Check if Proofing is supported for the file */
export function isProofingSupportedForFile(filename: string): boolean {
	return filename ? _.includes(sessionConstants.proofingSupportedFileTypes, filename.split(".").pop()
		.toLowerCase()) : false;
}

/**
 * Exception error class to use with validateFile function in case of prohibited file extension
 */
export class FileExtError extends Error { };

/**
 * Exception error class to use with validateFile function in case of image validation failure
 */
export class ImageMimeError extends Error { };

/**
 * Check that the file has acceptable file extension and signature if an image
 * Raise FileExtError in case of prohibited file extension
 * Raise ImageMimeError in case of image validation failure
 */
export async function validateFile(file: File): Promise<void> {
	const { excludedFileExtensions, exemptedFileExtensions } = sessionConstants;
	const fileExtension = getFileExtension(file);

	if (excludedFileExtensions.includes(fileExtension)) {
		// check if extensions are valid supported extensions
		throw new FileExtError("Extension failed");
	}

	if (!exemptedFileExtensions.includes(fileExtension) && isImageFile(file)) {
		const mimeValue = await getFileSignature(file);

		if (!checkImageSignature(mimeValue, fileExtension)) {
			throw new ImageMimeError("Mime test failed");
		}
	}
}

/**
* Check if the zip file contains valid numbered image files
* Also, since turntable zip might be very large, avoid loading and unzipping twice, get first image same time as validating
* set getFirstImage to true to receive blob url on Promise resolution
*/
export async function validateZipIsTurntable(file: File, getFirstImage: boolean, locale: ILocaleProvider): Promise<string> {
	const zipLoader = new JSZip();
	const zip = await zipLoader.loadAsync(file);
	const turntableImageNames = [];
	const standardImageNames = [];


	zip.forEach((path, file) => {
		// check if the file is the directory or not
		if (path.includes("__MACOSX") || path.includes("/._") || file.dir) {
			return;
		}

		if (RE_TURNTABLE.test(path)) {
			turntableImageNames.push(path);
		} else if (isImageFileName(path)) {
			standardImageNames.push(path);
		}
	});
	if (turntableImageNames.length == 0 && standardImageNames.length == 0) {
		throw new Error(locale.resource("SRV_ERR_SiteAdmin_ZIP_ERROR_NO_IMAGES", [file.name]));
	} else {
		const extensionSet = new Set(turntableImageNames.map(getFileNameExtension));
		const pathSet = turntableImageNames.map((item) => getTurntablePathGroup(item, 1));
		if (extensionSet.size > 1) {
			throw new Error(locale.resource("SRV_ERR_SiteAdmin_ZIP_ERROR_EXTENSION_MISMATCH", [file.name]));
		}
		if ((new Set(pathSet)).size > 1) {
			throw new Error(locale.resource("SRV_ERR_SiteAdmin_ZIP_ERROR_MULTIPLE_IMAGE_PATHS", [file.name]));
		}
		const imageNumbers = turntableImageNames.map((item) => getFileNameNumber(item, 2));
		// sort the imageNumbers array from smallest to largest
		imageNumbers.sort((a, b) => a - b);
		let expectedIndex = 1;
		if (!imageNumbers.every((elt, idx, arr) => {
			if (elt != expectedIndex) {
				const missingFile = expectedIndex + "." + getFileNameExtension(turntableImageNames[0]);
				throw new Error(locale.resource("SRV_ERR_SiteAdmin_MISSING_TURNTABLE_INDEX", [file.name, missingFile]));
			}
			expectedIndex += 1;
			var prev = arr[idx - 1];
			return !idx || elt === prev + 1;
		})) {
			throw new Error(locale.resource("SRV_ERR_SiteAdmin_ZIP_ERROR_NON_SEQUENTIAL_IMAGES", [file.name]));
		}
	}
	if (getFirstImage) {
		const firstImage = turntableImageNames.length > 0 ? turntableImageNames[0] : standardImageNames[0];
		const content = await zip.file(firstImage).async("arraybuffer");
		const buffer = new Uint8Array(content);
		const blob = new Blob([buffer.buffer]);
		const res = URL.createObjectURL(blob);
		return res;
	} else {
		return "";
	}
}

/**
 * validate image file and return the image
 * @param file - file to validate
 * @returns Promise, that resolves to image file data
 */
export async function validateImageFile(file: File): Promise<string> {
	return new Promise<string>((resolve, reject) => {
		const fileName = getFileName(file);
		if (isImageFileName(fileName)) {
			var reader = new FileReader();
			reader.onload = (e): void => {
				// validate if received data is image
				const res: string = e.target.result as string;
				if (res.indexOf("data:image") != 0) {
					// validation failed
					reject();
				} else {
					resolve(res);
				}
			};
			reader.readAsDataURL(file);
		} else {
			reject();
		}
	});
}

/**
 * Validate multiple files; if one or more files are not valid, then promise rejected with the list of invalid filenames
 */
export async function validateFiles(files: File[]): Promise<void> {
	return new Promise<void>(async (resolve, reject) => {
		const failed: string[] = [];

		for (const file of files) {
			await validateFile(file).catch(() => failed.push(getFileName(file)));
		}

		if (failed.length) {
			reject(failed);
		} else {
			resolve();
		}
	});
}
